import React, { useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Rating from '@mui/material/Rating';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import { getProductById, getProductsByCategory } from '../data/products';
import ProductCard from '../components/ProductCard';

function ProductDetail() {
  const { id } = useParams();
  const [quantity, setQuantity] = useState(1);
  const [tabValue, setTabValue] = useState(0);

  const product = getProductById(id);

  if (!product) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4">Product not found</Typography>
        <Button component={RouterLink} to="/products" sx={{ mt: 2 }}>
          Back to Products
        </Button>
      </Container>
    );
  }

  const relatedProducts = getProductsByCategory(product.categoryId)
    .filter((p) => p.id !== product.id)
    .slice(0, 4);

  const discount = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  return (
    <Box>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/" color="inherit" underline="hover">
            Home
          </Link>
          <Link component={RouterLink} to="/products" color="inherit" underline="hover">
            Shop
          </Link>
          <Link
            component={RouterLink}
            to={`/products/${product.categoryId}`}
            color="inherit"
            underline="hover"
          >
            {product.category}
          </Link>
          <Typography color="text.primary">{product.name}</Typography>
        </Breadcrumbs>

        <Grid container spacing={6}>
          {/* Product Image */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                position: 'relative',
                bgcolor: '#f8f8f8',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              {product.badge && (
                <Chip
                  label={product.badge}
                  color={product.badge === 'Sale' ? 'error' : 'primary'}
                  sx={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                    zIndex: 1,
                    fontWeight: 600,
                  }}
                />
              )}
              <Box
                component="img"
                src={product.image}
                alt={product.name}
                sx={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                }}
              />
            </Box>
          </Grid>

          {/* Product Info */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: 1 }}
            >
              {product.category}
            </Typography>
            
            <Typography variant="h3" sx={{ mt: 1, mb: 2 }}>
              {product.name}
            </Typography>

            {/* Rating */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <Rating value={product.rating} precision={0.1} readOnly />
              <Typography variant="body2" color="text.secondary">
                {product.rating} ({product.reviewCount} reviews)
              </Typography>
            </Box>

            {/* Price */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                ₹{product.price.toLocaleString()}
              </Typography>
              {product.originalPrice && (
                <>
                  <Typography
                    variant="h6"
                    sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
                  >
                    ₹{product.originalPrice.toLocaleString()}
                  </Typography>
                  <Chip label={`${discount}% OFF`} color="error" size="small" />
                </>
              )}
            </Box>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {product.description}
            </Typography>

            {/* Features */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Key Features
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {product.features.map((feature) => (
                  <Chip key={feature} label={feature} variant="outlined" size="small" />
                ))}
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Quantity and Add to Cart */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '1px solid #ddd',
                  borderRadius: 1,
                }}
              >
                <IconButton
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  size="small"
                >
                  <RemoveIcon />
                </IconButton>
                <Typography sx={{ px: 2, minWidth: 40, textAlign: 'center' }}>
                  {quantity}
                </Typography>
                <IconButton
                  onClick={() => setQuantity(quantity + 1)}
                  size="small"
                >
                  <AddIcon />
                </IconButton>
              </Box>

              <Button
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                sx={{ py: 1.5, fontWeight: 600 }}
              >
                Add to Cart - ₹{(product.price * quantity).toLocaleString()}
              </Button>
            </Box>

            {/* Benefits */}
            <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 2, p: 2 }}>
              {[
                { icon: LocalShippingOutlinedIcon, text: 'Free shipping on orders over ₹1,999' },
                { icon: VerifiedOutlinedIcon, text: '100-night sleep trial' },
                { icon: AutorenewOutlinedIcon, text: 'Easy returns & exchanges' },
              ].map((item) => (
                <Box
                  key={item.text}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, '&:last-child': { mb: 0 } }}
                >
                  <item.icon sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="body2">{item.text}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>

        {/* Tabs Section */}
        <Box sx={{ mt: 8 }}>
          <Tabs
            value={tabValue}
            onChange={(e, v) => setTabValue(v)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Description" />
            <Tab label="Specifications" />
            <Tab label="Reviews" />
          </Tabs>

          <Box sx={{ py: 4 }}>
            {tabValue === 0 && (
              <Box>
                <Typography variant="body1" paragraph>
                  {product.description}
                </Typography>
                <Typography variant="body1" paragraph>
                  Experience the perfect blend of comfort and support with our {product.name}. 
                  Designed with premium materials and advanced sleep technology, this pillow 
                  adapts to your unique sleeping position for optimal rest.
                </Typography>
                <Typography variant="body1">
                  Whether you're a side sleeper, back sleeper, or stomach sleeper, this pillow 
                  provides the right amount of support to keep your spine aligned and reduce 
                  pressure points throughout the night.
                </Typography>
              </Box>
            )}
            {tabValue === 1 && (
              <Grid container spacing={2}>
                {[
                  { label: 'Dimensions', value: 'Standard: 20" x 26" | King: 20" x 36"' },
                  { label: 'Fill Material', value: 'Premium Memory Foam / Down Blend' },
                  { label: 'Cover Material', value: '100% Organic Cotton' },
                  { label: 'Firmness', value: 'Medium (adjustable)' },
                  { label: 'Care', value: 'Machine washable cover' },
                  { label: 'Warranty', value: '5-year limited warranty' },
                ].map((spec) => (
                  <Grid item xs={12} sm={6} key={spec.label}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 120 }}>
                        {spec.label}:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {spec.value}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
            {tabValue === 2 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Typography variant="h3">{product.rating}</Typography>
                  <Box>
                    <Rating value={product.rating} precision={0.1} readOnly size="large" />
                    <Typography variant="body2" color="text.secondary">
                      Based on {product.reviewCount} reviews
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body1" color="text.secondary">
                  Customer reviews coming soon. Be the first to review this product!
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <Box sx={{ mt: 8 }}>
            <Typography variant="h4" sx={{ mb: 4 }}>
              You May Also Like
            </Typography>
            <Grid container spacing={3}>
              {relatedProducts.map((p) => (
                <Grid item xs={12} sm={6} md={3} key={p.id}>
                  <ProductCard product={p} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default ProductDetail;
